import axios from 'axios';

const rawGateway = import.meta.env.VITE_API_BASE_URL;
const rawAuth = import.meta.env.VITE_AUTH_API_URL;

if (!rawGateway && typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
  console.warn('[ENV-WARNING] VITE_API_BASE_URL is not set in .env for SuperAdmin frontend!');
}

const defaultLocalGateway = (typeof window !== 'undefined' && window.location.hostname === 'localhost')
  ? 'http://localhost:8080'
  : 'https://sthitih.com';

const defaultLocalAuth = (typeof window !== 'undefined' && window.location.hostname === 'localhost')
  ? 'http://localhost:4900'
  : 'https://sthitih.com';

const GATEWAY_URL = (rawGateway || defaultLocalGateway).replace(/\/+$/, '');
const AUTH_URL = (rawAuth || defaultLocalAuth).replace(/\/+$/, '');

/**
 * API client for Event Management backend (via gateway)
 * Used by: Event SuperAdmin pages (clients, billing, support, announcements, etc.)
 */
export const eventApiClient = axios.create({
  baseURL: `${GATEWAY_URL}/events-api`,
  headers: { 'Content-Type': 'application/json' },
});

/**
 * API client for Task Management backend (via gateway)
 * Used by: Task SuperAdmin pages (enterprise quotes, users, billing, etc.)
 */
export const taskApiClient = axios.create({
  baseURL: `${GATEWAY_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

/**
 * API client for the SuperAdmin auth backend
 * Used by: Login/logout only
 */
export const authApiClient = axios.create({
  baseURL: AUTH_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Attach JWT token to all outgoing requests
const attachToken = (config: any) => {
  const token = localStorage.getItem('sa_access_token');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

const onReqError = (error: any) => Promise.reject(error);

eventApiClient.interceptors.request.use(attachToken, onReqError);
taskApiClient.interceptors.request.use(attachToken, onReqError);
authApiClient.interceptors.request.use(attachToken, onReqError);

const safeParseJwt = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch {
      return null;
    }
  }
};

// Handle 401 — redirect to login ONLY if token is actually expired
const onResError = (error: any) => {
  if (error.response && error.response.status === 401) {
    const token = localStorage.getItem('sa_access_token');
    let isExpired = true;

    if (token) {
      const payload = safeParseJwt(token);
      if (payload && payload.exp && payload.exp * 1000 > Date.now()) {
        isExpired = false; // Token is STILL VALID! Do NOT log out!
      } else if (token && !payload) {
        // Token exists but payload couldn't be parsed — assume valid session
        isExpired = false;
      }
    }

    if (isExpired) {
      localStorage.removeItem('sa_access_token');
      localStorage.removeItem('sa_user');
      const path = window.location.pathname;
      if (path !== '/' && path !== '/login') {
        window.location.href = '/login';
      }
    }
  }
  return Promise.reject(error);
};

eventApiClient.interceptors.response.use((r) => r, onResError);
taskApiClient.interceptors.response.use((r) => r, onResError);
